import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Badge,
  Banner,
  Button,
  Input,
  Modal,
  Skeleton,
} from '@servicecompressionllc/sc-design-system';
import { ChevronRight } from '@servicecompressionllc/sc-design-system/icons';
import { AppShell } from '../../components/AppShell.js';
import { Pagination } from '../../components/Pagination.js';
import { ScopeSelector } from '../../components/ScopeSelector.js';
import { ScopeSummary } from '../../components/ScopeSummary.js';
import { useMe } from '../../hooks/useMe.js';
import { usePagination } from '../../hooks/usePagination.js';
import { useScopesLogic } from '../scopes/useScopesLogic.js';
import type { Group } from '../../lib/types.js';
import { useGroupsListLogic, useCreateGroupLogic } from './useGroupsLogic.js';

const PAGE_SIZE = 25;

const GroupsTableSkeleton = ({ rows = 4 }: { rows?: number }) => (
  <div className="bg-surface-elevated rounded-lg border border-outline p-4 space-y-3" role="status">
    {Array.from({ length: rows }, (_, i) => (
      <div key={i} className="flex items-center gap-4">
        <Skeleton className="h-4 w-40" />
        <Skeleton className="h-4 w-48" />
        <Skeleton className="h-5 w-10 ml-auto" />
      </div>
    ))}
  </div>
);

const CreateGroupModal = ({
  tenantId,
  onSuccess,
  onCancel,
}: {
  tenantId: string;
  onSuccess: () => void;
  onCancel: () => void;
}) => {
  const { scopes: catalog, isLoading: scopesLoading } = useScopesLogic();
  const logic = useCreateGroupLogic(tenantId, onSuccess);
  const canSubmit = !logic.isSubmitting && logic.name.trim().length > 0;

  const handleCreate = async () => {
    if (!canSubmit) return;
    try {
      await logic.submit();
      logic.reset();
    } catch {
      // surfaced via logic.error
    }
  };

  return (
    <Modal
      open
      onOpenChange={onCancel}
      className="flex max-h-[85vh] w-[calc(100%-2rem)] max-w-3xl flex-col overflow-hidden"
    >
      <Modal.Header>
        <Modal.Title>Create group</Modal.Title>
      </Modal.Header>
      <Modal.Body className="min-h-0 flex-1 overflow-y-auto">
        {logic.error && (
          <Banner variant="danger" className="mb-4">
            <Banner.Body>
              <Banner.Title>{logic.error.message}</Banner.Title>
            </Banner.Body>
          </Banner>
        )}
        <div className="space-y-4">
          <div>
            <label htmlFor="group-name" className="block text-sm font-medium text-ink mb-2">
              Name
            </label>
            <Input
              id="group-name"
              type="text"
              value={logic.name}
              onChange={(e) => logic.setName(e.target.value)}
              placeholder="e.g., Mechanics"
              disabled={logic.isSubmitting}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-ink mb-2">
              Scopes <span className="text-ink-muted">(optional — add later too)</span>
            </label>
            <ScopeSelector
              audience="user"
              scopes={catalog}
              selectedScopes={logic.scopes}
              onSelectedScopesChange={logic.setScopes}
              isLoading={scopesLoading}
              isDisabled={logic.isSubmitting}
            />
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onCancel} disabled={logic.isSubmitting}>
          Cancel
        </Button>
        <Button onClick={() => void handleCreate()} disabled={!canSubmit}>
          {logic.isSubmitting ? 'Creating...' : 'Create group'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export const GroupsPage = () => {
  const meQuery = useMe();
  const navigate = useNavigate();
  const tenantId = meQuery.data?.tenant.id;
  const { isLoading, data, error } = useGroupsListLogic(tenantId);
  const [showCreate, setShowCreate] = useState(false);
  const pager = usePagination(data ?? [], PAGE_SIZE);

  const openGroup = (group: Group) => navigate(`/settings/groups/${group.id}`);

  if (meQuery.isLoading || !meQuery.data) {
    return (
      <AppShell>
        <div className="mb-6">
          <Skeleton className="h-7 w-40 mb-2" />
          <Skeleton className="h-4 w-96" />
        </div>
        <GroupsTableSkeleton />
      </AppShell>
    );
  }

  return (
    <AppShell fill>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-ink">Groups</h2>
          <p className="text-ink-muted text-sm">
            Capability groups bundle scopes and apply them to every member. Select a group to manage
            its members and scopes.
          </p>
        </div>
        <Button onClick={() => setShowCreate(true)}>+ Create group</Button>
      </div>

      {error && (
        <Banner variant="danger" className="mb-4">
          <Banner.Body>
            <Banner.Title>{error.message}</Banner.Title>
          </Banner.Body>
        </Banner>
      )}

      {isLoading && <GroupsTableSkeleton />}

      {!isLoading && data && data.length === 0 && (
        <div className="text-center py-12 bg-surface-elevated rounded-lg border border-outline">
          <p className="text-ink-muted">No groups yet. Create one to get started.</p>
        </div>
      )}

      {!isLoading && data && data.length > 0 && (
        <div className="flex flex-col overflow-hidden rounded-lg border border-outline bg-surface-elevated">
          <div className="min-h-0 overflow-auto">
            <table className="w-full text-sm">
              <thead className="bg-surface-hover [&_th]:sticky [&_th]:top-0 [&_th]:z-10 [&_th]:bg-surface-hover">
                <tr className="text-left text-ink-muted">
                  <th className="px-4 py-3 font-medium">Name</th>
                  <th className="px-4 py-3 font-medium">Scopes</th>
                  <th className="px-4 py-3 font-medium">Members</th>
                  <th className="px-4 py-3" aria-hidden />
                </tr>
              </thead>
              <tbody>
                {pager.pageItems.map((group) => (
                  <tr
                    key={group.id}
                    role="link"
                    tabIndex={0}
                    onClick={() => openGroup(group)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        openGroup(group);
                      }
                    }}
                    className="cursor-pointer border-t border-outline hover:bg-surface focus-visible:bg-surface focus-visible:outline-none"
                  >
                    <td className="px-4 py-3 font-medium text-ink">{group.name}</td>
                    <td className="px-4 py-3">
                      <ScopeSummary scopes={group.scopes} />
                    </td>
                    <td className="px-4 py-3">
                      <Badge variant="info">{group.memberCount}</Badge>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <ChevronRight size={16} aria-hidden className="text-ink-subtle inline" />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Pagination
            className="border-t border-outline px-4 py-3"
            page={pager.page}
            pageCount={pager.pageCount}
            onPageChange={pager.setPage}
          />
        </div>
      )}

      {showCreate && (
        <CreateGroupModal
          tenantId={meQuery.data.tenant.id}
          onCancel={() => setShowCreate(false)}
          onSuccess={() => setShowCreate(false)}
        />
      )}
    </AppShell>
  );
};
